/**
 * @input string utc date
 */
export const utcToString = (date: string) => date.replace(/T/, ' ').replace(/\..+/, '');

export const getLocalTimezoneCode = (): string => {
  // Read the short timezone name (e.g. GMT+8) from the formatted parts,
  // falling back to resolvedOptions().timeZone if it's unavailable.
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts();
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Converts string from ISO-8601 UTC to human-readable format in device local timezone
 * i.e. YYYY-MM-DD HH:mm
 * @param utcDateString
 */
export const utcDateTimeToLocal = (utcDateString: string): string => {
  const dateObject = new Date(utcDateString);
  if (Number.isNaN(dateObject.getTime())) {
    return '';
  }
  // js doesn't offer a good way to display date in ISO format in local timezone
  // Idea: Create a new date object where time in UTC = desired local time and display that in ISO format and work from there
  const offsetDate = new Date(dateObject.getTime() - dateObject.getTimezoneOffset() * 60 * 1000);
  return offsetDate
    .toISOString()
    .substring(0, 16) // drop anything after minutes (i.e. seconds, milliseconds, timezone)
    .replace('T', ' '); // remove the T separating date and time
};

// currencies with zero decimal places
const zeroDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR'];

// getCurrencyView formats the amount based on currency and returns the currency and amount
export const getCurrencyView = (currency: string, amount?: number): string => {
  if (amount === undefined) {
    return '';
  }

  const formattedAmount = getAmountView(currency, amount);
  return `${currency} ${formattedAmount}`;
};

// getAmountView formats the amount based on currency and returns the amount only
export const getAmountView = (currency?: string, amount?: number): string => {
  if (amount === undefined) {
    return '';
  }

  // default to two-decimal currencies if currency is not provided
  const isZeroDecimal = zeroDecimalCurrencies.includes(currency?.toUpperCase() ?? '');
  const decimalDigits = isZeroDecimal ? 0 : 2;
  const divisor = isZeroDecimal ? 1 : 100;

  const formatter = Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalDigits,
  });

  return formatter.format(Math.round(amount) / divisor);
};
